import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { VStack, VTooltip, new__cn as cn, useShortcut } from '@/package';
import type { ToolMenuItem } from '@/store';

type ToolbarMenuProps = {
  disabled?: boolean;
  toolMenuItem?: Nullish<ToolMenuItem>;
  onClick?(toolMenuItem: ToolMenuItem): void;
};

const ToolbarMenu = ({
  children,
  disabled,
  toolMenuItem,
  className,

  onClick,

  ...rest
}: React.PropsWithChildren<
  ToolbarMenuProps & Omit<React.ComponentPropsWithRef<'div'>, 'onClick'>
>) => {
  const { t } = useTranslation();
  const { shortcutList } = useShortcut();
  const shortcutItem = useMemo(
    () => shortcutList.find(item => item.command === toolMenuItem?.command),
    [shortcutList, toolMenuItem],
  );

  if (!toolMenuItem || !shortcutItem) return null;

  const isDisabled = disabled ?? toolMenuItem.disabled;
  const isSelected = toolMenuItem.isSelected;
  const shortcutDescription = shortcutItem.description;

  const content = (
    <VStack
      {...rest}
      className={cn(
        'min-h-7 w-7 rounded-sm',
        isDisabled
          ? 'cursor-not-allowed bg-gray-200 text-gray-300'
          : {
              'bg-white text-primary-500': !isSelected,
              'bg-primary-500 text-skyBlue-200 ': isSelected,
            },
        '[&_svg]:size-6',
        className,
      )}
      role="button"
      justify="center"
      items="center"
      onClick={isDisabled ? () => {} : () => onClick?.(toolMenuItem)}
    >
      {children}
    </VStack>
  );

  if (!shortcutDescription) return content;

  return (
    <VTooltip content={<div>{t(shortcutDescription)}</div>} placement="right">
      {content}
    </VTooltip>
  );
};

ToolbarMenu.displayName = 'ToolbarMenu';

export default ToolbarMenu;
